import { Link } from "react-router-dom";
import { defineListView } from "@/base/list/defineListView";
import type { AuthedRequester } from "@/lib/brokerAuth";
import {
  listBondSubmissions,
  type BondSubmissionListItem,
} from "@/lib/brokerBonds";
import {
  BOND_DECISION_FILTER_OPTIONS,
  BOND_STATUS_FILTER_OPTIONS,
} from "@/lib/bondListLabels";
import { formatBondDate } from "@/lib/bondValueDisplay";
import { formatShortDate } from "@/lib/listFormat";
import { WORKFLOW_STATUS_OPTIONS } from "@/lib/workflowStatus";
import { BondDecisionChip, BondStatusChips } from "@/components/BondListCells";
import { buttonClass } from "@/components/buttonStyles";

// The brokerage's bond orders as a BaseListView definition (BRK-S253, after
// BRK-S088 and BRK-S174), drafts included: a draft's SB number resumes it.
// One definition for its two mounts, /surety/submissions and the Construction
// Bond landing, so the fetch, filters and states can't drift apart.
//
// What PMS applies is all it offers (`listBondSubmissionsForBrokerage`): the
// Status and Decision filters, the Stage filter (BRK-S183: PMS's
// post-submission `?workflowStatus=`, its OWN control and parameter, never new
// values on `?status=`, per SIG-S021 decision 7), the link-only `?accountId=` from an insured's
// profile (BRK-S140), and paging, newest first. It ignores search and sort,
// so there is no control for either until PMS-S590.

// The list's one call to action, on the one button style (BRK-S264).
const ACTION = buttonClass();

export function bondSubmissionsListView({
  request,
  emptyAction,
}: {
  request: AuthedRequester;
  /** Whether the first-run empty state offers "start your first". The
   *  landing already leads with Start submission, so it says so once. */
  emptyAction: boolean;
}) {
  return defineListView<BondSubmissionListItem>({
    endpoint: "/api/broker/bond/submissions",
    noun: "bond submissions",
    title: "Bond submissions",
    context: "Your brokerage’s surety bond orders — drafts included.",
    primaryAction: (
      <Link to="/surety/submissions/new" className={ACTION}>
        <span aria-hidden="true" className="text-base leading-none">
          +
        </span>
        New bond submission
      </Link>
    ),
    columns: [
      {
        id: "submission",
        header: "Submission",
        kind: "title",
        cell: (b) => <span className="tabular-nums">{b.number}</span>,
        subtitle: (b) => b.accountLegalName ?? undefined,
      },
      {
        id: "type",
        header: "Bond type",
        cell: (b) => b.bondSubproductName ?? b.bondProductName ?? "—",
      },
      {
        id: "created",
        header: "Created",
        cell: (b) => formatShortDate(b.createdAt),
      },
      {
        // The BID date, `m/d/yyyy` (D-10), and blank on a draft, which has
        // no filed bid yet.
        id: "bidDate",
        header: "Bid date",
        cell: (b) => (b.bidDate ? formatBondDate(b.bidDate) : "—"),
      },
      {
        id: "decision",
        header: "Decision",
        cell: (b) => <BondDecisionChip decision={b.decision} />,
      },
      {
        id: "status",
        header: "Status",
        cell: (b) => <BondStatusChips item={b} />,
      },
    ],
    rowKey: (b) => b.id,
    rowHref: (b) => `/surety/submissions/${b.id}`,
    rowLabel: (b) =>
      b.accountLegalName ? `${b.number}, ${b.accountLegalName}` : b.number,
    filters: [
      {
        urlKey: "status",
        apiParam: "status",
        label: "Status",
        allLabel: "All statuses",
        options: BOND_STATUS_FILTER_OPTIONS,
      },
      {
        urlKey: "decision",
        apiParam: "decision",
        label: "Decision",
        allLabel: "All decisions",
        options: BOND_DECISION_FILTER_OPTIONS,
      },
      {
        urlKey: "workflowStatus",
        apiParam: "workflowStatus",
        label: "Stage",
        allLabel: "All stages",
        options: WORKFLOW_STATUS_OPTIONS,
      },
    ],
    linkParams: [
      {
        urlKey: "accountId",
        apiParam: "accountId",
        note: "Showing one insured’s orders.",
      },
    ],
    pageSize: 25,
    empty: {
      title: "No bond submissions yet.",
      action: emptyAction ? (
        <Link
          to="/surety/submissions/new"
          className="text-sm font-medium text-brk-accent hover:underline"
        >
          Start your first bond submission →
        </Link>
      ) : undefined,
    },
    load: ({ filters, links, limit, offset }) =>
      listBondSubmissions(request, {
        // Narrowed from what BaseListView already checked against the options,
        // so the types say so too.
        status: BOND_STATUS_FILTER_OPTIONS.find(
          (o) => o.value === filters.status,
        )?.value,
        decision: BOND_DECISION_FILTER_OPTIONS.find(
          (o) => o.value === filters.decision,
        )?.value,
        workflowStatus: WORKFLOW_STATUS_OPTIONS.find(
          (o) => o.value === filters.workflowStatus,
        )?.value,
        accountId: links.accountId,
        limit,
        offset,
      }),
  });
}
